import React, { useState, useEffect } from 'react';
import { User, RefreshCw, ArrowLeft, LogOut, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AdminStatistics from './AdminStatistics';
import AdminJobs from './AdminJobs';
import { getApiBaseUrl } from '../config';
import './AdminPanel.css';

const API_URL = getApiBaseUrl();

const AdminPanel = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [username, setUsername] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Reset password state
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  
  // App state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeUsername, setShowChangeUsername] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Username change state
  const [usernameForm, setUsernameForm] = useState({
    new_username: '',
    password: ''
  });

  // Statistics (summary dashboard only)
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Verify token on mount (run once; verifyToken uses token from closure)
  useEffect(() => {
    if (token) {
      verifyToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when auth changes
  }, [isAuthenticated]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUsername(data.username);
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }
      
      const data = await response.json();
      localStorage.setItem('adminToken', data.token);
      setToken(data.token);
      setIsAuthenticated(true);
      setUsername(data.username);
      toast.success('Login successful');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken('');
    setIsAuthenticated(false);
    setUsername('');
    setApplications([]);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/request-password-reset?username=${resetUsername}`, {
        method: 'POST'
      });
      const data = await response.json();
      toast.success(data.message);
      setShowResetForm(false);
      setResetUsername('');
    } catch (error) {
      toast.error('Failed to request password reset');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }
      
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    
    if (!usernameForm.new_username.trim()) {
      toast.error('Please enter a new username');
      return;
    }
    
    if (usernameForm.new_username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/change-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_username: usernameForm.new_username,
          password: usernameForm.password
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change username');
      }
      
      toast.success('Username changed successfully. Please login again.');
      setShowChangeUsername(false);
      setUsernameForm({ new_username: '', password: '' });
      handleLogout();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch statistics');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load statistics');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Adel Group</h1>
            <p>Admin Panel</p>
          </div>
          
          {!showResetForm ? (
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  placeholder="Enter username"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="Enter password"
                  required
                />
              </div>
              <button type="submit" className="btn-login" disabled={loginLoading}>
                {loginLoading ? 'Logging in...' : 'Login'}
              </button>
              <button 
                type="button" 
                className="btn-forgot"
                onClick={() => setShowResetForm(true)}
              >
                Forgot Password?
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset} className="login-form">
              <p className="reset-info">Enter your username to receive a password reset email at info@adelgroups.com</p>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={resetUsername}
                  onChange={(e) => setResetUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                />
              </div>
              <button type="submit" className="btn-login">
                Request Reset
              </button>
              <button 
                type="button" 
                className="btn-forgot"
                onClick={() => setShowResetForm(false)}
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title-section">
            <a href="/" className="back-link">
              <ArrowLeft size={20} />
              Back to Website
            </a>
            <p className="admin-subtitle">Summary dashboard · Logged in as: {username}</p>
          </div>
          <div className="header-actions">
            <button className="btn-icon" onClick={() => setShowChangeUsername(true)} title="Change Username">
              <User size={20} />
            </button>
            <button className="btn-icon" onClick={() => setShowChangePassword(true)} title="Change Password">
              <Lock size={20} />
            </button>
            <button className="btn-refresh" onClick={fetchStatistics} disabled={statsLoading}>
              <RefreshCw size={18} className={statsLoading ? 'spinning' : ''} />
              Refresh
            </button>
            <button className="btn-logout" onClick={handleLogout}>
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <AdminStatistics stats={stats} statsLoading={statsLoading} token={token} apiUrl={API_URL} />

      <AdminJobs token={token} apiUrl={API_URL} />

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2><Lock size={20} /> Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowChangePassword(false)}>Cancel</button>
                <button type="submit" className="btn-save">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Username Modal */}
      {showChangeUsername && (
        <div className="modal-overlay" onClick={() => setShowChangeUsername(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2><User size={20} /> Change Username</h2>
            <form onSubmit={handleChangeUsername}>
              <div className="form-group">
                <label>New Username</label>
                <input
                  type="text"
                  value={usernameForm.new_username}
                  onChange={(e) => setUsernameForm({...usernameForm, new_username: e.target.value})}
                  placeholder="Enter new username"
                  required
                />
              </div>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={usernameForm.password}
                  onChange={(e) => setUsernameForm({...usernameForm, password: e.target.value})}
                  placeholder="Enter your password to confirm"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowChangeUsername(false)}>Cancel</button>
                <button type="submit" className="btn-save">Change Username</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
